import isError from 'lodash/isError';
import { useAccountConfig } from '@/vue/store/useAccountConfig';
const accountConfigStore = useAccountConfig();

declare let $: any;

// keycode mappings
enum keyCodes {
    enter = 13,
	tab = 9,
	delete = 46,
	esc = 27,
	space = 32,
	up = 38,
	down = 40,
	left = 37,
	right = 39,
	end = 35,
	home = 36,
	del = 46,
	backspace = 8,
	insert = 45,
	pageup = 33,
	pagedown = 34
}

const deepClone = (src: any): any => {
    return {
        ...src
    };
};

const isEqualObject = (src: object, target: object): boolean => {
    return JSON.stringify(src) === JSON.stringify(target);
};

const Enum = (arr: Array<any>): object => {
    const enumObj = {};
    arr.forEach(function(key, i) {
        enumObj[enumObj[key] = i + 1] = key;
    });

    return Object.freeze(enumObj);
};

const onDocReady = (fn: any): void => {
    // see if DOM is already available
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
        // call on next available tick
        setTimeout(fn, 1);
    } else {
        document.addEventListener('DOMContentLoaded', fn);
    }
};

const getCookie = (cname: string): string => {
    const name = `${cname  }=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for(let i = 0; i <ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length);
        }
    }
    return '';
};

const setCookie = (cname: string, cvalue: string, exdays = 3000): void => {
    const d = new Date();
    d.setTime(d.getTime() + (exdays*24*60*60*1000));
    const expires = `expires=${ d.toUTCString()}`;
    document.cookie = `${cname}=${cvalue}; ${expires}; secure`;
};

/**
 * generates a quick non-secure hash
 * @param {string} string the string from which to generate a hash code
 * @returns {string}
 */
const hashCode = (string: string): string => {
    let hash = 0; let i; let chr;
    for (i = 0; i < string.length; i++) {
        chr   = string.charCodeAt(i);
        hash  = ((hash << 5) - hash) + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
};

/**
 * reads the data attribute "data-language" from the html element, which is set in the master page
 * @returns {string}
 */
const getLanguageId = (): string => {
    const el = document.documentElement;
    
    // attribute set in master page code behind
    return el.getAttribute('data-language');
};

/**
 * Looks for class "is-english" on the html element which is set in the master page
 * @returns {boolean}
 */
const isEnglish = (): boolean => {
    return $('html').hasClass('is-english');
};

/**
 * Looks if the user is viewing Certify through a mobile device and determines which function
 * to use since certify mobile cannot open a new tab in the cordova inappbrowser
 */
const openInNewTab = (url: string): void => {
    if (accountConfigStore.accountConfig.hasOwnProperty('IsCertifyMobile') && !!accountConfigStore.accountConfig.IsCertifyMobile) {
        window.location.assign(url);
        return;
    }
    window.open(url);
};

/**
 * Looks to see if the user is logged in
 * @returns {boolean}
 */
const isUserLoggedIn = (): boolean => {
    return !!window.AccountConfig?.Emp?.PK;
}

/**
 * Converts a string containing a regular expression into an array
 * of two strings -- 0 representing the regex op, 1 representing 
 * the match
 * 
 * @param regexp string containing the regex
 * @returns {string[]}
 */
const friendlyRegex = (regexp: string): string[] => {
    const data = [];

    if (regexp.startsWith('^')) {
        //Starts With
        data.push('Starts With');
        data.push(
            regexp
                .replace('^', '')
                .replace('.*', '')
                .replace('\\.', '.')
        );
    } else if (regexp.endsWith('$')) {
        //Ends With
        data.push('Ends With');
        data.push(regexp.replace('$', '').replace('.*', '').replace('\\.','.'));
    } else if (regexp.startsWith('.*') && regexp.endsWith('.*')) {
        //Contains
        data.push('Contains');
        data.push(regexp.replace(/\.\*/g, '').replace('\\.', '.'));
    } else {
        data.push('Full Match');
        data.push(regexp.replace('\\.', '.'));
    }

    return data;
};

const getQueryStringParam = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams: any, prop: string) => searchParams.get(prop),
});

const wait = function (ms = 1000) {
    return new Promise(resolve => {
        setTimeout(resolve, ms);
    });
};

class PollingTimeoutException extends Error {
    constructor() {
        super(); 
        this.name = 'PollingTimeoutException'; 
    }
}

const poll = async function (fn: () => Promise<unknown>, fnCondition: (result: unknown) => boolean, pollInterval = 2000, maxAttempts = 15) {
    let failedNetworkAttempts = 0;
    let attempts = 1;
    let result = await fn()
        .catch(() => failedNetworkAttempts++);
    while (fnCondition(result)) {
        await wait(pollInterval);

        if (attempts > maxAttempts) {
            throw new PollingTimeoutException();
        }

        try {
            result = await fn();
            failedNetworkAttempts = 0;
        } catch(ex) {
            failedNetworkAttempts++;
            if (failedNetworkAttempts > 1) {
                throw ex
            }
        }
        attempts++;
    }
    return result;
};


export {
    keyCodes,
    deepClone,
    isEqualObject,
    Enum,
    onDocReady,
    getCookie,
    setCookie,
    hashCode,
    getLanguageId,
    isEnglish,
    openInNewTab,
    isUserLoggedIn,
    friendlyRegex,
    getQueryStringParam,
    PollingTimeoutException,
    poll,
};